import { DiceRollGameEvent, GameState, Player } from '../types/game';
import { computeScore } from '../domain/scoring';
import { canRescueByFive } from '../domain/dice';
import { makeEmptyDiceMask } from '../domain/gameRules';

export function deriveFirstRollInfoFromEvents(state: GameState, playerId: string) {
  const events = state.events || [];
  const firstRoll = [...events].reverse().find((e): e is DiceRollGameEvent =>
    e.type === 'DICE_ROLL'
    && e.turn === state.turnNumber
    && e.payload.playerId === playerId
    && e.payload.rollIndex === 0
  );
  if (!firstRoll) return null;
  const payload = firstRoll.payload;
  const dice: number[] = payload.dice || [];
  const keptMask: boolean[] = payload.keptMask || [];
  const rolledDice = dice.filter((_, idx) => !keptMask[idx]);
  const sc = rolledDice.length
    ? computeScore(rolledDice)
    : { basePoints: 0, scoringDiceCount: 0, breakdown: [], breakdownDetails: [] };
  const bust = rolledDice.length > 0 && sc.scoringDiceCount === 0;
  const rescueEnabled = state.settings?.rescueByFive !== undefined ? state.settings.rescueByFive : true;
  const rescueAvailable = bust && rescueEnabled && canRescueByFive(rolledDice, 0);
  return {
    rollIndex: payload.rollIndex,
    scoringDiceCount: sc.scoringDiceCount || 0,
    bust,
    didBust: bust && !rescueAvailable,
  };
}

export const pushNotice = (state: GameState, notice: import('../types/game').GameNotice): GameState => {
  const existing = state.notices || [];
  const next = [...existing, notice].slice(-8);
  return { ...state, notices: next };
};

export function ensureOnlineTurnCycle(state: GameState): GameState {
  if (state.mode === 'REAL') return state;
  const gameStarted = state.lobby?.phase === 'IN_GAME'
    || (state.events || []).some((ev) => ev.type === 'GAME_STARTED');
  if (!gameStarted) return state;
  const tc = state.turnCycle;
  const hasActive = Boolean(tc?.activePlayerId);
  if (tc && hasActive) return state;

  const order = state.lobby?.order || [];
  const currentIdx = state.currentPlayerIndex ?? 0;
  const candidateFromCurrent = state.players[currentIdx]?.id || null;
  const candidateFromOrder = order.length > 0
    ? order[Math.min(currentIdx, order.length - 1)] || order[0]
    : null;
  const fallbackFirst = state.players[0]?.id || null;
  const activeId = tc?.activePlayerId || candidateFromCurrent || candidateFromOrder || fallbackFirst;
  if (!activeId) return state;

  const normalizedIndex = state.players.findIndex((p) => p.id === activeId);
  return {
    ...state,
    currentPlayerIndex: normalizedIndex >= 0 ? normalizedIndex : state.currentPlayerIndex,
    turnCycle: {
      activePlayerId: activeId,
      rollCount: tc?.rollCount ?? 0,
      dice: tc?.dice ?? [],
      kept: tc?.kept ?? makeEmptyDiceMask(),
      capturedMask: tc?.capturedMask ?? makeEmptyDiceMask(),
      status: tc?.status ?? 'IDLE',
      lastRoll: tc?.lastRoll,
      diceOrigins: tc?.diceOrigins,
    },
  };
}

export const withDefaultStatus = (player: Player): Player => ({
  ...player,
  status: player.status || 'active',
  role: player.role || 'player',
});

const isActivePlayer = (player?: Player | null): boolean => {
  if (!player) return false;
  return (player.status || 'active') === 'active';
};

const getActiveTurnOrder = (state: GameState): string[] => {
  const baseOrder = (state.lobby?.order && state.lobby.order.length > 0)
    ? state.lobby.order
    : state.players.map((p) => p.id);
  return baseOrder.filter((id) => {
    const player = state.players.find((p) => p.id === id);
    return isActivePlayer(player);
  });
};

const findNextActivePlayerId = (state: GameState, currentId?: string | null): string | null => {
  const order = getActiveTurnOrder(state);
  if (order.length === 0) return null;
  if (!currentId) return order[0] || null;
  const idx = order.findIndex((id) => id === currentId);
  if (idx === -1) return order[0] || null;
  const nextIdx = (idx + 1) % order.length;
  return order[nextIdx] || null;
};

export const computeTurnAdvance = (
  state: GameState,
  players: Player[],
  currentPlayerId: string | null
): { nextPlayerId: string | null; nextTurnNumber: number } => {
  const working: GameState = { ...state, players };
  const order = getActiveTurnOrder(working);
  if (order.length === 0) {
    return { nextPlayerId: null, nextTurnNumber: state.turnNumber };
  }
  const currentOrderIdx = currentPlayerId ? order.findIndex((id) => id === currentPlayerId) : -1;
  const nextId = findNextActivePlayerId(working, currentPlayerId);
  const nextOrderIdx = nextId ? order.findIndex((id) => id === nextId) : -1;
  const wrapped = currentOrderIdx !== -1 && nextOrderIdx !== -1 && nextOrderIdx <= currentOrderIdx;
  const nextTurnNumber = wrapped ? state.turnNumber + 1 : state.turnNumber;
  return { nextPlayerId: nextId, nextTurnNumber };
};

export function updatePlayerPositions(players: Player[]): Player[] {
  const ranking = players.filter((p) => p.role !== 'viewer');
  const orderIndex = new Map(players.map((p, idx) => [p.id, idx]));
  const sortedPlayers = [...ranking].sort((a, b) => {
    const scoreDiff = b.score - a.score;
    if (scoreDiff !== 0) return scoreDiff;
    const aTie = typeof a.position === 'number' && a.position > 0
      ? a.position
      : (orderIndex.get(a.id) ?? 0) + 1;
    const bTie = typeof b.position === 'number' && b.position > 0
      ? b.position
      : (orderIndex.get(b.id) ?? 0) + 1;
    return aTie - bTie;
  });
  return players.map((player) => {
    if (player.role === 'viewer') {
      return { ...player, position: player.position || players.length + 1 };
    }
    return { ...player, position: sortedPlayers.findIndex((p) => p.id === player.id) + 1 };
  });
}
